import { useState } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import { errorToast } from "@/v2-ui/toast"
import { ConfirmButton } from "@/v2-backoffice-client/components/button"
import { useUserEmailDuplicateMergeMutation } from "@/v2-backoffice-client/user/emailDuplicate/user.emailDuplicate.merge.mutation"
import {
  describeDuplicateLogins,
  describeMergeEffects,
} from "@/v2-backoffice-client/user/emailDuplicate/userEmailDuplicate.utils"
import type { UserEmailDuplicateFragment } from "@/v2-backoffice-client/user/emailDuplicate/__types__/user.emailDuplicate.fragment"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"

export type UserEmailDuplicateKeepCellProps = {
  row: UserEmailDuplicateFragment
  /** The other live rows with this email: what gets merged into this one. */
  others: UserEmailDuplicateFragment[]
  /** Whether this row is the one the board suggests keeping (signed in, most teams, oldest). */
  isSuggested: boolean
  /** The rows are gone (one stays, without a duplicate) once merged; the board refetches. */
  onMerged?(): void
}

/**
 * The Keep cell: one button per row, "Keep this one", that merges every
 * other row of the email into it. The suggested row's button is filled
 * in, the others outlined, so the choice is visible before the click and
 * the confirmation says exactly what follows the person onto the kept row
 * and what stops working.
 */
function UserEmailDuplicateKeepCell(props: UserEmailDuplicateKeepCellProps) {
  const { row, others, isSuggested, onMerged } = props

  const [isRunning, setIsRunning] = useState(false)
  const [merge, { error }] = useUserEmailDuplicateMergeMutation()
  errorToast(error, { title: "Failed to merge the duplicate users" })

  const keptName = formatUserName(row.FirstName, row.LastName)
  const retiredIds = others.map((other) => `#${other.Id}`).join(", ")

  const effects = others.map((other) => describeMergeEffects(row, other))
  const moves = [...new Set(effects.flatMap((effect) => effect.moves))]
  const drops = [...new Set(effects.flatMap((effect) => effect.drops))]
  const signedIn = row.LastSignInAt
    ? `signed in ${formatDateString(row.LastSignInAt)}`
    : "never signed in"

  const confirm = [
    `Keep ${keptName} #${row.Id} (${describeDuplicateLogins(row)}, ${signedIn}) and merge ${retiredIds} into it?`,
    moves.length > 0
      ? `${capitalize(joinWithAnd(moves))} follow${moves.length === 1 ? "s" : ""} onto #${row.Id}.`
      : null,
    drops.length > 0
      ? `#${row.Id} already has ${drops.length === 1 ? "that kind of login" : "those logins"}, so the merged ${drops.length === 1 ? "row's" : "rows'"} ${drops.join(" and ")} stop${drops.length === 1 ? "s" : ""} working.`
      : null,
    `${retiredIds} ${others.length === 1 ? "is" : "are"} removed.`,
  ]
    .filter((part): part is string => part !== null)
    .join(" ")

  return (
    <ConfirmButton
      size="xs"
      variant={isSuggested ? "suggested" : "outline"}
      iconSrc="user"
      isLoading={isRunning}
      content={
        isSuggested
          ? `The row to keep, by the board's reading: ${signedIn}, ${row.TenantCount} team${row.TenantCount === 1 ? "" : "s"}. ${retiredIds} merge into it.`
          : `Keep this row instead and merge ${retiredIds} into it.`
      }
      confirmContent={confirm}
      confirmVariant="suggested"
      onConfirm={async () => {
        setIsRunning(true)
        try {
          for (const other of others) {
            const result = await merge({
              variables: { keepUserId: row.Id, retireUserId: other.Id },
            })
            // errorPolicy is "all" app-wide: a rejected write resolves with
            // the error (toasted above) instead of throwing; stop there.
            if (result.errors?.length || !result.data?.mergeDuplicateUsers) {
              return
            }
          }
          onMerged?.()
        } finally {
          setIsRunning(false)
        }
      }}
    >
      Keep this one
    </ConfirmButton>
  )
}

// "the Fusion login, its 2 team memberships and the HubSpot contact link".
function joinWithAnd(parts: string[]) {
  if (parts.length <= 1) return parts.join("")
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`
}

function capitalize(sentence: string) {
  return sentence.charAt(0).toUpperCase() + sentence.slice(1)
}

export default UserEmailDuplicateKeepCell
